import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API_CACHE_TTL, createApiGetCacheKey, fetchCachedGetApi, getCachedApiValue, isAbortError } from "@/utils/apiCache";

export const ALBUM_DETAIL_ENDPOINT = "/.netlify/functions/SpotifyGetArtistOneAlbum";
export const ALBUM_DETAIL_CONCURRENCY = 4;

export interface CatalogAlbum {
  id: string;
  name: string;
  images?: Array<{ url: string }>;
  release_date: string;
  album_type: string;
  album_group?: string;
  total_tracks: number;
  artists?: Array<{ id?: string; name: string }>;
}

export interface AlbumDetail {
  id?: string;
  artists?: Array<{ id?: string; name: string }>;
  total_tracks?: number;
  tracks?: { items?: any[] };
}

export type AlbumDetailState = "queued" | "loading" | "done" | "failed";

interface AlbumDetailResponse {
  album?: AlbumDetail;
}

export const albumDetailParams = (artistSlug: string, album: Pick<CatalogAlbum, "id" | "name">) => ({
  artist: artistSlug,
  album: album.name,
  id: album.id,
});

export const cachedAlbumDetail = (artistSlug: string, album: Pick<CatalogAlbum, "id" | "name">): AlbumDetail | null => {
  const cached = getCachedApiValue<AlbumDetailResponse>(createApiGetCacheKey(ALBUM_DETAIL_ENDPOINT, albumDetailParams(artistSlug, album)));
  const detail = cached?.album;
  return detail?.tracks?.items?.length && (!detail.id || detail.id === album.id) ? detail : null;
};

export function planAlbumQueue(queue: string[], ids: string[], started: ReadonlySet<string>, priority: boolean): string[] {
  const fresh = ids.filter((id, index) => !started.has(id) && ids.indexOf(id) === index);
  if (!fresh.length) return queue;
  if (priority) return [...fresh, ...queue.filter((id) => !fresh.includes(id))];
  return [...queue, ...fresh.filter((id) => !queue.includes(id))];
}

export function useAlbumDetailQueue(artistSlug: string, albums: CatalogAlbum[] | null, concurrency = ALBUM_DETAIL_CONCURRENCY) {
  const [details, setDetails] = useState<Record<string, AlbumDetail>>({});
  const [states, setStates] = useState<Record<string, AlbumDetailState>>({});
  const albumsById = useMemo(() => new Map((albums || []).map((album) => [album.id, album])), [albums]);
  const albumsRef = useRef(albumsById);
  albumsRef.current = albumsById;
  const queueRef = useRef<string[]>([]);
  const startedRef = useRef<Set<string>>(new Set());
  const activeRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const pumpRef = useRef<() => void>(() => undefined);

  const resolved = useMemo(() => {
    const result: Record<string, AlbumDetail> = {};
    for (const album of albums || []) {
      const detail = details[album.id] || cachedAlbumDetail(artistSlug, album);
      if (detail) result[album.id] = detail;
    }
    return result;
  }, [albums, artistSlug, details]);

  pumpRef.current = () => {
    const controller = controllerRef.current;
    if (!controller) return;
    while (activeRef.current < concurrency && queueRef.current.length) {
      const id = queueRef.current.shift() as string;
      const album = albumsRef.current.get(id);
      if (!album || startedRef.current.has(id)) continue;
      startedRef.current.add(id);
      activeRef.current += 1;
      setStates((current) => ({ ...current, [id]: "loading" }));
      fetchCachedGetApi<AlbumDetailResponse>(ALBUM_DETAIL_ENDPOINT, albumDetailParams(artistSlug, album), {
        ttlMs: API_CACHE_TTL.SIX_HOURS,
        shouldCache: (data) => Boolean(data?.album?.tracks?.items?.length),
        signal: controller.signal,
      })
        .then((data) => {
          if (controller.signal.aborted) return;
          const detail = data?.album;
          const usable = Boolean(detail?.tracks?.items?.length) && (!detail?.id || detail.id === id);
          if (usable) setDetails((current) => ({ ...current, [id]: detail as AlbumDetail }));
          setStates((current) => ({ ...current, [id]: usable ? "done" : "failed" }));
        })
        .catch((error) => {
          if (controller.signal.aborted || isAbortError(error)) return;
          setStates((current) => ({ ...current, [id]: "failed" }));
        })
        .finally(() => {
          if (controllerRef.current !== controller) return;
          activeRef.current -= 1;
          pumpRef.current();
        });
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    pumpRef.current();
    return () => {
      controller.abort();
      controllerRef.current = null;
      activeRef.current = 0;
      queueRef.current = [];
      startedRef.current = new Set();
    };
  }, [artistSlug]);

  const enqueue = useCallback(
    (ids: string[], priority: boolean) => {
      const wanted = ids.filter((id) => {
        const album = albumsRef.current.get(id);
        return album && !cachedAlbumDetail(artistSlug, album);
      });
      queueRef.current = planAlbumQueue(queueRef.current, wanted, startedRef.current, priority);
      if (!wanted.length) return;
      setStates((current) => {
        const next = { ...current };
        for (const id of wanted) if (!next[id]) next[id] = "queued";
        return next;
      });
      pumpRef.current();
    },
    [artistSlug],
  );

  const request = useCallback((ids: string[]) => enqueue(ids, true), [enqueue]);
  const requestAll = useCallback(() => enqueue([...albumsRef.current.keys()], false), [enqueue]);

  const settledCount = (albums || []).filter((album) => resolved[album.id] || states[album.id] === "failed").length;

  return {
    details: resolved,
    states,
    request,
    requestAll,
    settledCount,
    total: albums?.length || 0,
    complete: Boolean(albums) && settledCount === (albums?.length || 0),
  };
}

export default useAlbumDetailQueue;
